import React from 'react';
import { ShipWheel } from 'lucide-react';
import { useTheme } from '@/components/theme-provider';
import slackColorIcon from '../assets/icons/slack-color.png';
import { ReactComponent as GCPIcon } from '../assets/icons/gcp.svg';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';
import railwayIcon from '../assets/icons/railway.svg';
import flyioIcon from '../assets/icons/flyio.svg';
import nebiusIcon from '../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../assets/icons/clickhouse.svg';
import jenkinsIcon from '../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';
import databricksIcon from '../assets/icons/databricks.png';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../assets/icons/infisical.svg';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';
import jiraIcon from '../assets/icons/jira.svg';
import pagerdutyColorIcon from '../assets/icons/pagerduty-icon.svg';
import awsLightLogo from '../assets/icons/aws.svg';
import awsDarkLogo from '../assets/icons/aws-dark.svg';
import argocdIcon from '../assets/icons/argocd.svg';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import confluenceIcon from '../assets/icons/confluence.svg';
import ociIcon from '../assets/icons/oci.png';
import linearIcon from '../assets/icons/linear.png';
import linearDarkIcon from '../assets/icons/linear-dark-mode.png';
import gleanIcon from '../assets/icons/glean.svg';
import notionIcon from '../assets/icons/notion-logo-no-background.png';
import notionLightIcon from '../assets/icons/notion-logo-white.png';
import cloudflareIcon from '../assets/icons/cloudflare.svg';
import fluxcdIcon from '../assets/icons/flux-cd.svg';
import karpenterIcon from '../assets/icons/karpenter.svg';
import kyvernoIcon from '../assets/icons/kyverno.svg';
import trivyIcon from '../assets/icons/trivy.svg';
import helmLightIcon from '../assets/icons/helm-light.svg';
import helmDarkIcon from '../assets/icons/helm-dark.svg';
import datadogLightIcon from '../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../assets/icons/datadog-dark-mode.png';
import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';

interface IntegrationIconProps {
  /** Integration catalog key (e.g. "cloudflare", "github") */
  integration: string;
  /** Icon square size in px (default 16) */
  size?: number;
  className?: string;
}

// Keys handled by the switch in IntegrationIcon below. Keep in sync when adding
// new cases so callers can test icon availability without rendering.
const SUPPORTED_INTEGRATION_ICONS = new Set([
  'kubernetes', 'aws', 'aws-cost', 'gcp', 'gcp-cost', 'gcp-security', 'gcp-data', 'oci', 'cloudflare', 'nebius', 'github', 'gitlab',
  'jenkins', 'circleci', 'sonarcloud', 'okta', 'databricks', 'terraform', 'pulumi', 'argocd', 'argo-rollouts', 'fluxcd',
  'karpenter', 'kyverno', 'trivy',
  'helm', 'vercel', 'railway', 'flyio', 'beam', 'daytona', 'supabase', 'planetscale',
  'neon', 'clickhouse', 'slack', 'pagerduty', 'datadog', 'confluence', 'jira',
  'linear', 'notion', 'glean', 'posthog', 'vault', 'infisical', 'kestrel',
]);

/** Whether IntegrationIcon renders a real brand icon for this key (vs null). */
export function hasIntegrationIcon(integration: string): boolean {
  return SUPPORTED_INTEGRATION_ICONS.has(integration);
}

/**
 * Brand icon for an integration, keyed by integration catalog key.
 * Theme-aware: swaps light/dark variants where the brand requires it.
 */
export function IntegrationIcon({ integration, size = 16, className }: IntegrationIconProps) {
  const { theme } = useTheme();
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const s: React.CSSProperties = { width: size, height: size };

  switch (integration) {
    case 'kubernetes':
      return <ShipWheel style={s} className={`text-blue-500 ${className || ''}`} />;
    case 'aws':
    case 'aws-cost':
    case 'aws-ops':
    case 'aws-security':
    case 'aws-data':
      return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" style={s} className={`object-contain ${className || ''}`} />;
    case 'gcp':
    case 'gcp-cost':
    case 'gcp-security':
    case 'gcp-data':
      return <GCPIcon style={s} className={`object-contain ${className || ''}`} />;
    case 'oci':
      return <img src={ociIcon} alt="OCI" style={s} className={`object-contain ${className || ''}`} />;
    case 'cloudflare':
      return <img src={cloudflareIcon} alt="Cloudflare" style={s} className={`object-contain ${className || ''}`} />;
    case 'nebius':
      return <img src={nebiusIcon} alt="Nebius" style={s} className={`object-contain ${className || ''}`} />;
    case 'github':
      return (
        <img
          src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'}
          alt="GitHub"
          style={s}
          className={`object-contain ${className || ''}`}
        />
      );
    case 'gitlab':
      return <img src={gitlabIcon} alt="GitLab" style={s} className={`object-contain ${className || ''}`} />;
    case 'jenkins':
      return <img src={jenkinsIcon} alt="Jenkins" style={s} className={`object-contain ${className || ''}`} />;
    case 'circleci':
      return <CircleCIIcon style={s} className={`text-foreground ${className || ''}`} />;
    case 'sonarcloud':
      return <SonarCloudIcon style={s} className={`text-foreground ${className || ''}`} />;
    case 'okta':
      return <OktaIcon style={s} className={`text-foreground ${className || ''}`} />;
    case 'databricks':
      return <img src={databricksIcon} alt="Databricks" style={s} className={`object-contain ${className || ''}`} />;
    case 'terraform':
      return <img src={terraformIcon} alt="Terraform" style={s} className={`object-contain ${className || ''}`} />;
    case 'vault':
      return <img src={vaultIcon} alt="Vault" style={s} className={`object-contain ${className || ''}`} />;
    case 'infisical':
      return <img src={infisicalIcon} alt="Infisical" style={s} className={`object-contain rounded-sm ${className || ''}`} />;
    case 'pulumi':
      return <img src={pulumiIcon} alt="Pulumi" style={s} className={`object-contain ${className || ''}`} />;
    case 'argocd':
    case 'argo-rollouts':
      return <img src={argocdIcon} alt="Argo" style={s} className={`object-contain ${className || ''}`} />;
    case 'fluxcd':
      return <img src={fluxcdIcon} alt="Flux CD" style={s} className={`object-contain ${className || ''}`} />;
    case 'karpenter':
      return <img src={karpenterIcon} alt="Karpenter" style={s} className={`object-contain ${className || ''}`} />;
    case 'kyverno':
      return <img src={kyvernoIcon} alt="Kyverno" style={s} className={`object-contain ${className || ''}`} />;
    case 'trivy':
      return <img src={trivyIcon} alt="Trivy" style={s} className={`object-contain ${className || ''}`} />;
    case 'helm':
      return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="Helm" style={s} className={`object-contain ${className || ''}`} />;
    case 'vercel':
      return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" style={s} className={`object-contain ${className || ''}`} />;
    case 'railway':
      return <img src={railwayIcon} alt="Railway" style={{ ...s, filter: isDark ? 'invert(1)' : 'none' }} className={`object-contain ${className || ''}`} />;
    case 'flyio':
      return <img src={flyioIcon} alt="Fly.io" style={s} className={`object-contain ${className || ''}`} />;
    case 'beam':
      return <BeamIcon style={s} className={`text-foreground ${className || ''}`} />;
    case 'daytona':
      return <DaytonaIcon style={s} className={`text-foreground ${className || ''}`} />;
    case 'supabase':
      return <img src={supabaseIcon} alt="Supabase" style={s} className={`object-contain ${className || ''}`} />;
    case 'planetscale':
      return <PlanetScaleIcon style={s} className={`text-foreground ${className || ''}`} />;
    case 'neon':
      return <img src={neonIcon} alt="Neon" style={s} className={`object-contain ${className || ''}`} />;
    case 'clickhouse':
      return <img src={clickhouseIcon} alt="ClickHouse" style={s} className={`object-contain ${className || ''}`} />;
    case 'slack':
      return <img src={slackColorIcon} alt="Slack" style={s} className={`object-contain ${className || ''}`} />;
    case 'pagerduty':
      return <img src={pagerdutyColorIcon} alt="PagerDuty" style={s} className={`object-contain ${className || ''}`} />;
    case 'datadog':
      return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" style={s} className={`object-contain ${className || ''}`} />;
    case 'confluence':
      return <img src={confluenceIcon} alt="Confluence" style={s} className={`object-contain ${className || ''}`} />;
    case 'jira':
      return <img src={jiraIcon} alt="Jira" style={s} className={`object-contain ${className || ''}`} />;
    case 'linear':
      return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" style={s} className={`object-contain ${className || ''}`} />;
    case 'notion':
      return <img src={isDark ? notionLightIcon : notionIcon} alt="Notion" style={s} className={`object-contain ${className || ''}`} />;
    case 'glean':
      return <img src={gleanIcon} alt="Glean" style={s} className={`object-contain ${className || ''}`} />;
    case 'posthog':
      return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" style={s} className={`object-contain ${className || ''}`} />;
    case 'kestrel':
      return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" style={s} className={`object-contain ${className || ''}`} />;
    default:
      return null;
  }
}

export default IntegrationIcon;
